// 2026-06-26: Phase 14 — Material Certificates
// Mill / heat-lot certificates per material. Linked to one or more WOs.
// Data lives in customer SERVER_SCOPE attribute `material_certs` (array).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { customerbasedshift, cleanCustomerId } from "../../Services/app/operatorservice";
import { shiftadd } from "../../Services/app/masterservice";
import Swal from 'sweetalert2';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, MenuItem, Tooltip, IconButton, Chip, Autocomplete,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VerifiedIcon from '@mui/icons-material/Verified';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LinkIcon from '@mui/icons-material/Link';

const BR = {
  orange: '#f97316', blue: '#2563eb', green: '#16a34a', amber: '#f59e0b', red: '#dc2626',
  dark: '#111827', muted: '#6b7280', border: '#e5e7eb', bg: '#f9fafb',
};

const MATERIAL_SPECS = [
  'AMS 5510 (304L SS)','AMS 5513 (316L SS)','AMS 5644 (17-4 PH)',
  'AMS 4928 (Ti-6Al-4V)','AMS 4911 (Ti-6Al-4V sheet)',
  'AMS 5662 (Inconel 718)','AMS 5663 (Inconel 718 bar)',
  'AMS 6265 (4340)','AMS 6414 (4340M)',
  'AMS 4027 (6061-T6)','AMS 4117 (6061-T651)','AMS 4150 (7075-T6)',
  'AISI 1018 Steel','AISI 4140 Steel','Other',
];

const FORMS = ['Bar','Plate','Sheet','Round','Forging','Casting','Tube','Wire'];

const STATUS_INFO = {
  VALID:   { color: BR.green, bg: '#dcfce7', label: 'VALID',   icon: <VerifiedIcon fontSize="small" /> },
  WARNING: { color: BR.amber, bg: '#fef3c7', label: 'EXPIRING', icon: <WarningAmberIcon fontSize="small" /> },
  EXPIRED: { color: BR.red,   bg: '#fee2e2', label: 'EXPIRED', icon: <ErrorOutlineIcon fontSize="small" /> },
  NA:      { color: BR.muted, bg: '#f3f4f6', label: 'NO EXPIRY', icon: <VerifiedIcon fontSize="small" /> },
};

function computeStatus(expiry) {
  if (!expiry) return 'NA';
  const e = new Date(expiry);
  if (isNaN(e.getTime())) return 'NA';
  const days = (e - new Date()) / 86400000;
  if (days < 0) return 'EXPIRED';
  if (days <= 30) return 'WARNING';
  return 'VALID';
}

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

const emptyCert = {
  id: '',
  heat_no: '',
  cert_no: '',
  material_spec: '',
  material_grade: '',
  form: '',
  supplier: '',
  cert_date: '',
  received_date: '',
  expiry_date: '',
  attached_url: '',
  test_summary: '',
  linked_wo_numbers: [],
  qty_kg: '',
  notes: '',
};

export default function MaterialCerts() {
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID') || window._env_.CUSTOMER_ID);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState(emptyCert);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const r = await customerbasedshift(customerId, 'material_certs');
      const v = Array.isArray(r?.[0]?.value) ? r[0].value : [];
      setItems(v);
    } catch (e) { setItems([]); } finally { setLoading(false); }
  }, [customerId]);

  useEffect(() => { refresh(); }, [refresh]);

  const enriched = useMemo(() => items.map(i => ({ ...i, status: computeStatus(i.expiry_date) })), [items]);

  const totals = useMemo(() => {
    const out = { ALL: enriched.length, VALID: 0, WARNING: 0, EXPIRED: 0, NA: 0 };
    enriched.forEach(i => { out[i.status] = (out[i.status] || 0) + 1; });
    return out;
  }, [enriched]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enriched.filter(i => {
      if (filter !== 'ALL' && i.status !== filter) return false;
      if (!q) return true;
      return (i.heat_no || '').toLowerCase().includes(q)
          || (i.cert_no || '').toLowerCase().includes(q)
          || (i.material_spec || '').toLowerCase().includes(q)
          || (i.supplier || '').toLowerCase().includes(q)
          || (i.linked_wo_numbers || []).join(' ').toLowerCase().includes(q);
    }).sort((a, b) => (b.received_date || '').localeCompare(a.received_date || ''));
  }, [enriched, filter, search]);

  function openAdd() { setDraft({ ...emptyCert, id: `mat-${Date.now()}` }); setEditOpen(true); }
  function openEdit(item) { setDraft({ ...emptyCert, ...item }); setEditOpen(true); }

  async function save() {
    if (!draft.heat_no || !draft.material_spec) {
      Swal.fire({ icon: 'warning', title: 'Heat # and material spec required', confirmButtonColor: BR.orange });
      return;
    }
    setSaving(true);
    try {
      const exists = items.findIndex(i => i.id === draft.id);
      const next = exists === -1 ? [...items, draft]
                                 : items.map((it, idx) => idx === exists ? draft : it);
      await shiftadd({ material_certs: next }, customerId, 'SERVER_SCOPE');
      setItems(next); setEditOpen(false);
      Swal.fire({ toast: true, position: 'top-end', timer: 1800, showConfirmButton: false,
                   icon: 'success', title: `${draft.heat_no} saved` });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: e.message, confirmButtonColor: BR.orange });
    } finally { setSaving(false); }
  }

  async function deleteItem(item) {
    const res = await Swal.fire({
      title: 'Delete certificate?',
      html: `<strong>Heat ${item.heat_no}</strong><br/>${item.material_spec}<br/>${(item.linked_wo_numbers || []).length} WOs linked.`,
      icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Delete', confirmButtonColor: BR.red,
      cancelButtonText: 'Cancel', reverseButtons: true, focusCancel: true,
    });
    if (!res.isConfirmed) return;
    const next = items.filter(i => i.id !== item.id);
    await shiftadd({ material_certs: next }, customerId, 'SERVER_SCOPE');
    setItems(next);
  }

  return (
    <div style={{ padding: 16, background: BR.bg, minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: BR.dark }}>
            📜 Material Certificates
          </h1>
          <div style={{ fontSize: 12, color: BR.muted, marginTop: 4 }}>
            Mill / heat-lot certificates · linked to WOs · NADCAP audit input
          </div>
        </div>
        <button onClick={openAdd}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '9px 16px', background: BR.orange, color: '#fff',
                          border: 'none', borderRadius: 6, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
          <AddIcon fontSize="small" /> Add Certificate
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
        {[
          { key: 'ALL',     label: 'All', icon: '📋', color: BR.dark },
          { key: 'VALID',   label: 'Valid', icon: '✓', color: BR.green },
          { key: 'WARNING', label: 'Expiring', icon: '⚠', color: BR.amber },
          { key: 'EXPIRED', label: 'Expired', icon: '✗', color: BR.red },
        ].map(t => (
          <div key={t.key} onClick={() => setFilter(t.key)}
               style={{ background: '#fff', border: `2px solid ${filter === t.key ? t.color : BR.border}`,
                        borderRadius: 8, padding: '12px 14px', cursor: 'pointer' }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t.icon} {t.label}
            </div>
            <div style={{ fontSize: 26, fontWeight: 900, color: t.color, lineHeight: 1.1, marginTop: 4 }}>
              {totals[t.key] ?? 0}
            </div>
          </div>
        ))}
      </div>

      <input value={search} onChange={e => setSearch(e.target.value)}
             placeholder="Search heat #, cert #, spec, supplier, WO #…"
             style={{ width: '100%', padding: '8px 12px', border: `1px solid ${BR.border}`,
                       borderRadius: 6, fontSize: 13, marginBottom: 10 }} />

      <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: BR.muted }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: BR.muted, fontSize: 13 }}>
            No certificates {filter !== 'ALL' ? `in ${filter} state` : 'yet'}. Click "Add Certificate" to start.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: BR.bg, borderBottom: `1px solid ${BR.border}` }}>
                {['Status','Heat #','Cert #','Material Spec','Form','Supplier','Received','Cert Date','Expiry','Linked WOs',''].map(h => (
                  <th key={h} style={{ padding: '9px 10px', textAlign: 'left', fontSize: 10,
                                        fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(i => {
                const info = STATUS_INFO[i.status] || STATUS_INFO.NA;
                return (
                  <tr key={i.id} style={{ borderBottom: `1px solid ${BR.border}` }}>
                    <td style={{ padding: '9px 10px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                                      padding: '2px 8px', borderRadius: 4, fontWeight: 800, fontSize: 10,
                                      background: info.bg, color: info.color, border: `1px solid ${info.color}` }}>
                        {info.icon} {info.label}
                      </span>
                    </td>
                    <td style={{ padding: '9px 10px', fontFamily: 'monospace', fontWeight: 800 }}>{i.heat_no}</td>
                    <td style={{ padding: '9px 10px', fontFamily: 'monospace', color: BR.muted }}>
                      {i.attached_url ? (
                        <a href={i.attached_url} target="_blank" rel="noreferrer"
                           style={{ color: BR.blue, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                          {i.cert_no || '—'} <LinkIcon fontSize="small" sx={{ fontSize: 13 }} />
                        </a>
                      ) : (i.cert_no || '—')}
                    </td>
                    <td style={{ padding: '9px 10px' }}>
                      <div style={{ fontWeight: 700 }}>{i.material_spec || '—'}</div>
                      {i.material_grade && <div style={{ fontSize: 10, color: BR.muted }}>{i.material_grade}</div>}
                    </td>
                    <td style={{ padding: '9px 10px', color: BR.muted }}>{i.form || '—'}</td>
                    <td style={{ padding: '9px 10px' }}>{i.supplier || '—'}</td>
                    <td style={{ padding: '9px 10px' }}>{fmtDate(i.received_date)}</td>
                    <td style={{ padding: '9px 10px' }}>{fmtDate(i.cert_date)}</td>
                    <td style={{ padding: '9px 10px', color: info.color, fontWeight: 700 }}>{fmtDate(i.expiry_date)}</td>
                    <td style={{ padding: '9px 10px' }}>
                      {(i.linked_wo_numbers || []).slice(0, 3).map(wo => (
                        <Chip key={wo} label={wo} size="small"
                              sx={{ height: 18, fontSize: 9, fontWeight: 700, fontFamily: 'monospace',
                                     background: '#eff6ff', color: '#1e3a8a', border: '1px solid #93c5fd',
                                     marginRight: 0.3, marginBottom: 0.3 }} />
                      ))}
                      {(i.linked_wo_numbers || []).length > 3 && (
                        <span style={{ fontSize: 10, color: BR.muted }}>+{i.linked_wo_numbers.length - 3}</span>
                      )}
                    </td>
                    <td style={{ padding: '9px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(i)}>
                        <EditIcon fontSize="small" sx={{ color: '#111' }} />
                      </IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => deleteItem(i)}>
                        <DeleteIcon fontSize="small" sx={{ color: BR.red }} />
                      </IconButton></Tooltip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: BR.muted, padding: '8px 12px',
                     background: '#fff7ed', border: `1px solid #fed7aa`, borderRadius: 6 }}>
        <strong style={{ color: '#9a3412' }}>NADCAP note:</strong> An auditor will trace any finished part back to its mill/heat certificate.
        Link the WO number here at goods receipt so the Audit Package generator can attach the cert URL alongside operation history.
      </div>

      {/* Add / Edit */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {items.some(i => i.id === draft.id) ? `Edit ${draft.heat_no}` : 'Add Material Certificate'}
        </DialogTitle>
        <DialogContent dividers>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            <TextField label="Heat # *" size="small" fullWidth
                       value={draft.heat_no}
                       onChange={e => setDraft({ ...draft, heat_no: e.target.value })}
                       placeholder="e.g. H-2025-4477" />
            <TextField label="Certificate #" size="small" fullWidth
                       value={draft.cert_no}
                       onChange={e => setDraft({ ...draft, cert_no: e.target.value })} />
            <TextField label="Material Spec *" size="small" fullWidth select
                       value={draft.material_spec}
                       onChange={e => setDraft({ ...draft, material_spec: e.target.value })}>
              <MenuItem value="">— Select —</MenuItem>
              {MATERIAL_SPECS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
            <TextField label="Grade" size="small" fullWidth
                       value={draft.material_grade}
                       onChange={e => setDraft({ ...draft, material_grade: e.target.value })} />
            <TextField label="Form" size="small" fullWidth select
                       value={draft.form}
                       onChange={e => setDraft({ ...draft, form: e.target.value })}>
              <MenuItem value="">— Select —</MenuItem>
              {FORMS.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
            </TextField>
            <TextField label="Qty (kg)" size="small" fullWidth type="number"
                       value={draft.qty_kg}
                       onChange={e => setDraft({ ...draft, qty_kg: e.target.value })} />
            <TextField label="Supplier" size="small" fullWidth
                       value={draft.supplier}
                       onChange={e => setDraft({ ...draft, supplier: e.target.value })}
                       placeholder="e.g. Jindal Stainless" />
            <TextField label="Certificate URL" size="small" fullWidth
                       value={draft.attached_url}
                       onChange={e => setDraft({ ...draft, attached_url: e.target.value })}
                       placeholder="https://drive.example/cert-h2025-4477.pdf" />
            <TextField label="Cert Date" size="small" fullWidth type="date"
                       value={draft.cert_date}
                       InputLabelProps={{ shrink: true }}
                       onChange={e => setDraft({ ...draft, cert_date: e.target.value })} />
            <TextField label="Received Date" size="small" fullWidth type="date"
                       value={draft.received_date}
                       InputLabelProps={{ shrink: true }}
                       onChange={e => setDraft({ ...draft, received_date: e.target.value })} />
            <TextField label="Expiry / Shelf-life End" size="small" fullWidth type="date"
                       value={draft.expiry_date}
                       InputLabelProps={{ shrink: true }}
                       onChange={e => setDraft({ ...draft, expiry_date: e.target.value })} />
            <Autocomplete multiple freeSolo size="small" fullWidth
                          options={[]} value={draft.linked_wo_numbers || []}
                          onChange={(_, v) => setDraft({ ...draft, linked_wo_numbers: v })}
                          renderInput={(params) => (
                            <TextField {...params} label="Linked WO numbers" placeholder="WO-2026-…" />
                          )} />
            <TextField label="Test Summary" size="small" fullWidth multiline minRows={2}
                       value={draft.test_summary}
                       onChange={e => setDraft({ ...draft, test_summary: e.target.value })}
                       placeholder="e.g. UTS 540 MPa, YS 250 MPa, Elong 50%"
                       sx={{ gridColumn: '1 / span 2' }} />
            <TextField label="Notes" size="small" fullWidth multiline minRows={2}
                       value={draft.notes}
                       onChange={e => setDraft({ ...draft, notes: e.target.value })}
                       sx={{ gridColumn: '1 / span 2' }} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} variant="contained"
                  sx={{ background: BR.orange, '&:hover': { background: '#ea580c' } }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
